import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { customDashboardApi, formsApi, reportApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, BarChart3, Users, Clock, Loader2,
  Star, CheckCircle, TrendingUp, Table2, Search, Eye, Download
} from 'lucide-react';

export default function CustomDashboardView() {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState({});
  const [expandedForm, setExpandedForm] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [reportWidgets, setReportWidgets] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await customDashboardApi.get(dashboardId);
        setDashboard(res.data);
        // Load report widget data
        const widgets = res.data.widgets || [];
        for (const w of widgets) {
          if (w.type === 'report' && w.report_id) {
            try {
              const rpt = await reportApi.get(w.report_id);
              setReportWidgets(prev => ({ ...prev, [w.report_id]: rpt.data }));
            } catch {}
          }
        }
      } catch { toast.error('Failed to load dashboard'); navigate('/my-dashboards'); }
      finally { setLoading(false); }
    };
    load();
  }, [dashboardId]);

  const loadTableData = async (formId) => {
    if (expandedForm === formId) { setExpandedForm(null); return; }
    setTableLoading(true);
    setExpandedForm(formId);
    try {
      const res = await formsApi.submissionsTable(formId);
      setTableData(prev => ({ ...prev, [formId]: res.data }));
    } catch { toast.error('Failed to load table data'); }
    finally { setTableLoading(false); }
  };

  const exportCsv = async (formId, formName) => {
    try {
      const res = await formsApi.submissionsCsv(formId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formName.replace(/\s+/g, '_')}_submissions.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;
  if (!dashboard) return null;

  const color = dashboard.color || '#0E7490';
  const totalSubs = dashboard.stats?.total_submissions || 0;
  const totalForms = dashboard.forms?.length || 0;

  return (
    <div className="space-y-6" data-testid="dashboard-view-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/my-dashboards')}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-3 h-8 rounded-full" style={{ backgroundColor: color }} />
            <h1 className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>{dashboard.name}</h1>
            {dashboard.is_homepage && <Star className="w-4 h-4 text-[#CA8A04] fill-[#CA8A04]" />}
          </div>
          {dashboard.description && <p className="text-sm text-[#64748B] ml-6">{dashboard.description}</p>}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Total Forms" value={totalForms} color={color} testId="stat-total-forms" />
        <StatCard icon={CheckCircle} label="Submissions" value={totalSubs} color="#166534" testId="stat-total-subs" />
        <StatCard icon={TrendingUp} label="Avg / Form" value={totalForms > 0 ? Math.round(totalSubs / totalForms) : 0} color="#7C3AED" testId="stat-avg" />
        <StatCard icon={Users} label="Active Forms" value={dashboard.forms?.filter(f => f.status === 'published').length || 0} color="#CA8A04" testId="stat-active" />
      </div>

      {/* Form Cards */}
      <div>
        <h2 className="text-lg font-semibold text-[#0A2342] mb-3">Forms</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(dashboard.forms || []).map((form) => (
            <Card key={form.id} className="careiq-card overflow-hidden hover:shadow-md transition-shadow" data-testid={`form-widget-${form.id}`}>
              <div className="h-1.5" style={{ backgroundColor: form.category_color || '#64748B' }} />
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0A2342] text-sm">{form.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      {form.category && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded text-white font-medium" style={{ backgroundColor: form.category_color || '#64748B' }}>
                          {form.category}
                        </span>
                      )}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${form.status === 'published' ? 'bg-[#166534]/15 text-[#166534]' : 'bg-[#64748B]/15 text-[#64748B]'}`}>
                        {form.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{ color: form.category_color || color }}>{form.submissions_count}</div>
                    <div className="text-[10px] text-[#64748B]">submissions</div>
                  </div>
                </div>

                <div className="text-xs text-[#64748B]">{form.fields_count} fields</div>

                {/* Recent submissions */}
                {form.recent_submissions?.length > 0 && (
                  <div className="border-t border-[#E2E8F0] pt-2">
                    <div className="text-[10px] text-[#94A3B8] mb-1">Recent Activity</div>
                    {form.recent_submissions.slice(0, 3).map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px] text-[#64748B] py-0.5">
                        <Clock className="w-2.5 h-2.5" />
                        <span>{new Date(s.submitted_at).toLocaleDateString()}</span>
                        <span className="ml-auto px-1 py-0.5 bg-[#F1F5F9] rounded text-[9px]">{s.submitted_by_role}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-1 pt-1">
                  <Link to={`/forms/${form.id}/fill`} className="flex-1">
                    <Button size="sm" className="w-full text-white text-xs" style={{ backgroundColor: form.category_color || color }} data-testid={`fill-form-${form.id}`}>
                      <FileText className="w-3 h-3 mr-1" />Fill Form
                    </Button>
                  </Link>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => loadTableData(form.id)} data-testid={`view-table-${form.id}`}>
                    <Table2 className="w-3 h-3 mr-1" />{expandedForm === form.id ? 'Hide' : 'Data'}
                  </Button>
                  <Link to={`/forms/builder?id=${form.id}`}>
                    <Button size="sm" variant="outline" className="text-xs">Edit</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Data Table for expanded form */}
      {expandedForm && (
        <Card className="careiq-card" data-testid="submissions-data-table">
          <CardContent className="p-4">
            {tableLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" /></div>
            ) : tableData[expandedForm] ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-[#0A2342]">
                    <Table2 className="w-4 h-4 inline mr-2 text-[#0E7490]" />
                    {tableData[expandedForm].form_name} — {tableData[expandedForm].total} Records
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-[#94A3B8]" />
                      <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search records..." className="pl-8 h-8 w-48 text-xs" data-testid="table-search" />
                    </div>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => exportCsv(expandedForm, tableData[expandedForm]?.form_name || 'export')} data-testid="export-csv-btn">
                      <Download className="w-3 h-3 mr-1" />Export CSV
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto border border-[#E2E8F0] rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#F1F5F9]">
                        {tableData[expandedForm].columns.map((col) => (
                          <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-[#64748B] border-b whitespace-nowrap">{col.label}</th>
                        ))}
                        <th className="px-3 py-2 text-left text-xs font-medium text-[#64748B] border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData[expandedForm].rows
                        .filter(row => {
                          if (!searchTerm) return true;
                          const term = searchTerm.toLowerCase();
                          return Object.values(row).some(v => String(v).toLowerCase().includes(term));
                        })
                        .map((row, ri) => (
                        <tr key={row.id || ri} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`table-row-${ri}`}>
                          {tableData[expandedForm].columns.map((col) => (
                            <td key={col.key} className="px-3 py-2 text-xs text-[#0A2342] max-w-[200px] truncate">
                              {col.key === 'submitted_at' ? (row[col.key] ? new Date(row[col.key]).toLocaleString() : '') : (row[col.key] || '—')}
                            </td>
                          ))}
                          <td className="px-3 py-2">
                            <Link to={`/forms/${expandedForm}/submissions/${row.id}/edit`}>
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-[#0E7490]" data-testid={`edit-row-${ri}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {tableData[expandedForm].rows.length === 0 && <p className="text-center py-6 text-sm text-[#94A3B8]">No submissions yet</p>}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Report Widgets */}
      {(dashboard.widgets || []).filter(w => w.type === 'report').length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-[#0A2342] mb-3">Report Widgets</h2>
          <div className="space-y-4">
            {(dashboard.widgets || []).filter(w => w.type === 'report').map((w, i) => {
              const rpt = reportWidgets[w.report_id];
              if (!rpt) return null;
              return (
                <Card key={i} className="careiq-card" data-testid={`report-widget-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-[#0A2342] text-sm">{rpt.name} <span className="text-[10px] text-[#64748B] ml-1">({rpt.report_type.replace(/_/g, ' ')})</span></h3>
                      <Link to="/reports"><Button size="sm" variant="ghost" className="text-xs text-[#0E7490]"><Eye className="w-3 h-3 mr-1" />View Full Report</Button></Link>
                    </div>
                    {w.widget_type === 'stat' && rpt.data?.summary ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(rpt.data.summary).map(([k, v]) => (
                          <div key={k} className="p-3 bg-[#F1F5F9] rounded-lg text-center">
                            <div className="text-lg font-bold text-[#0E7490]">{typeof v === 'number' ? v.toLocaleString() : String(v)}</div>
                            <div className="text-[10px] text-[#64748B]">{k.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    ) : rpt.data?.columns ? (
                      <div className="overflow-x-auto border rounded-lg max-h-[300px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="bg-[#F1F5F9] sticky top-0">
                            {rpt.data.columns.slice(0, 6).map(c => <th key={c.key} className="px-3 py-2 text-left text-xs font-medium text-[#64748B] border-b whitespace-nowrap">{c.label}</th>)}
                          </tr></thead>
                          <tbody>
                            {(rpt.data.rows || []).slice(0, 10).map((row, ri) => (
                              <tr key={ri} className="border-b hover:bg-[#F8FAFC]">
                                {rpt.data.columns.slice(0, 6).map(c => (
                                  <td key={c.key} className="px-3 py-2 text-xs max-w-[150px] truncate">
                                    {c.key === 'submitted_at' ? (row[c.key] ? new Date(row[c.key]).toLocaleDateString() : '') : (row[c.key] || '—')}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-[#94A3B8]">{rpt.data?.total_submissions || rpt.data?.total || 0} records</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <Card className="careiq-card" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <div className="text-2xl font-bold text-[#0A2342]">{value}</div>
          <div className="text-xs text-[#64748B]">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
